import { Modal, type ModalProps, Typography, Select } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type DeleteUserMutation } from '../../../api/mutations';
import { ApplicationStage, type ApplicationStageDetails } from '../../../api/types';
import {
    useReferenceApplicationContext,
    ReferenceApplicationProvider,
} from '../../shared/ApplicationDetailsPage/standard/ApplicationTab/ReferenceApplicationField';

const StickyFooterModal = styled(Modal)`
    & .ant-modal-footer {
        position: sticky;
        bottom: 0;
        background: white;
        height: 100%;
    }
`;

const DropdownWrapper = styled.div`
    margin-bottom: 16px;
`;

type AssignedEntity = {
    urlSlug: string;
    identifier: string;
    applicationStage?: ApplicationStage;
    type: 'application' | 'module' | 'event';
};

const IdentifierLink = ({ identifier, path }: { identifier: string; path: string }) => (
    <Typography.Link href={path} target="_blank">
        {identifier}
    </Typography.Link>
);

const CommaSeparatedIdentifiers = ({ entities }: { entities: AssignedEntity[] }) => {
    const paths = useReferenceApplicationContext();

    const getApplicationPath = useCallback(
        (entity: AssignedEntity) => {
            if (entity.type === 'application' && paths[entity.applicationStage]) {
                return `${paths[entity.applicationStage]}/${entity.urlSlug}`;
            }

            return null;
        },
        [paths]
    );

    const getEventPath = useCallback((eventId: string) => `/admin/events/${eventId}`, []);
    const getModulePath = useCallback((moduleId: string) => `/admin/system/modules/${moduleId}`, []);

    const getLink = useCallback(
        (entity: AssignedEntity) => {
            switch (entity.type) {
                case 'event':
                    return <IdentifierLink identifier={entity.identifier} path={getEventPath(entity.urlSlug)} />;
                case 'application':
                    return <IdentifierLink identifier={entity.identifier} path={getApplicationPath(entity)} />;
                case 'module':
                    return <IdentifierLink identifier={entity.identifier} path={getModulePath(entity.urlSlug)} />;
                default:
                    return null;
            }
        },
        [getEventPath, getApplicationPath, getModulePath]
    );

    return (
        <>
            {entities.map((entity, index) => (
                <span key={entity.identifier}>
                    {getLink(entity)}
                    {index < entities.length - 1 && ', '}
                </span>
            ))}
        </>
    );
};

type NewAssigneeDropdownProps = {
    availableAssignees: DeleteUserMutation['result']['availableAssignees'];
    currentAssigneeId: string;
    onAssign: (userId: string) => void;
};

const NewAssigneeDropdown = ({ availableAssignees, currentAssigneeId, onAssign }: NewAssigneeDropdownProps) => {
    const { t } = useTranslation(['userDetails']);

    if (availableAssignees.length === 0) {
        return null;
    }

    return (
        <DropdownWrapper>
            <Typography.Text strong>{t('userDetails:deleteUser.assignTo')}</Typography.Text>
            <Select
                onChange={onAssign}
                placeholder={t('userDetails:deleteUser.assignToPlaceholder')}
                style={{ width: '100%' }}
                value={currentAssigneeId}
            >
                {availableAssignees.map(({ id, displayName }) => (
                    <Select.Option key={id} value={id}>
                        {displayName}
                    </Select.Option>
                ))}
            </Select>
        </DropdownWrapper>
    );
};

type DeleteUserModalProps = {
    open: boolean;
    onClose: () => void;
    displayCompany: (id: string) => boolean;
    userId: string;
    deleteUserResult?: DeleteUserMutation['result'];
    onRetry?: (newAssigneeId: string) => void;
};

const DeleteUserModal = ({
    open,
    onClose,
    deleteUserResult,
    displayCompany,
    userId,
    onRetry,
}: DeleteUserModalProps) => {
    const { t } = useTranslation(['userDetails']);
    const [newAssigneeId, setNewAssigneeId] = useState<string>();

    if (!deleteUserResult) {
        return null;
    }

    const { message, assigned } = deleteUserResult ?? {};
    const { applications, modules, events } = assigned ?? {};

    const createStageObject = (
        stage: ApplicationStageDetails,
        applicationStage: ApplicationStage,
        suiteId: string
    ): AssignedEntity =>
        stage?.assigneeId === userId
            ? {
                  type: 'application',
                  applicationStage,
                  identifier: stage?.identifier,
                  urlSlug: suiteId,
              }
            : null;

    const remaining = {
        applications: applications
            ?.filter(({ module: { companyId } }) => displayCompany(companyId))
            .flatMap(
                ({
                    leadStage,
                    mobilityStage,
                    reservationStage,
                    financingStage,
                    appointmentStage,
                    insuranceStage,
                    versioning: { suiteId },
                }) =>
                    [
                        createStageObject(leadStage, ApplicationStage.Lead, suiteId),
                        createStageObject(mobilityStage, ApplicationStage.Mobility, suiteId),
                        createStageObject(reservationStage, ApplicationStage.Reservation, suiteId),
                        createStageObject(financingStage, ApplicationStage.Financing, suiteId),
                        createStageObject(appointmentStage, ApplicationStage.Appointment, suiteId),
                        createStageObject(insuranceStage, ApplicationStage.Insurance, suiteId),
                    ].filter(Boolean)
            ),

        modules: modules
            ?.filter(({ companyId }) => displayCompany(companyId))
            .map<AssignedEntity>(module => ({
                identifier: module.displayName,
                urlSlug: module.id,
                type: 'module',
            })),
        events: events
            ?.filter(({ module: { companyId } }) => displayCompany(companyId))
            .map<AssignedEntity>(event => ({
                identifier: event.displayName,
                urlSlug: event.id,
                type: 'event',
            })),
    };

    const content = (
        <ReferenceApplicationProvider
            referencePaths={{
                stages: {
                    [ApplicationStage.Appointment]: '/admin/appointments',
                    [ApplicationStage.Financing]: '/admin/applications',
                    [ApplicationStage.Insurance]: '/admin/insurances',
                    [ApplicationStage.Lead]: '/admin/leads',
                    [ApplicationStage.Reservation]: '/admin/reservations',
                },
            }}
        >
            <Typography.Paragraph>{message}</Typography.Paragraph>

            <NewAssigneeDropdown
                availableAssignees={deleteUserResult.availableAssignees}
                currentAssigneeId={newAssigneeId}
                onAssign={setNewAssigneeId}
            />

            {!!remaining.applications?.length && (
                <>
                    <Typography.Text strong>{t('userDetails:deleteUser.assigned.applications')}</Typography.Text>
                    <Typography.Paragraph>
                        <CommaSeparatedIdentifiers entities={remaining.applications} />
                    </Typography.Paragraph>
                </>
            )}
            {!!remaining.modules?.length && (
                <>
                    <Typography.Text strong>{t('userDetails:deleteUser.assigned.modules')}</Typography.Text>
                    <Typography.Paragraph>
                        <CommaSeparatedIdentifiers entities={remaining.modules} />
                    </Typography.Paragraph>
                </>
            )}
            {!!remaining.events?.length && (
                <>
                    <Typography.Text strong>{t('userDetails:deleteUser.assigned.events')}</Typography.Text>
                    <Typography.Paragraph>
                        <CommaSeparatedIdentifiers entities={remaining.events} />
                    </Typography.Paragraph>
                </>
            )}
        </ReferenceApplicationProvider>
    );

    return (
        <StickyFooterModal
            okButtonProps={{ disabled: !newAssigneeId }}
            onCancel={onClose}
            onOk={() => {
                onClose();
                onRetry(newAssigneeId);
            }}
            open={open}
            style={{ maxHeight: '80vh', overflowY: 'auto', paddingBottom: 0 }}
            title={t('userDetails:deleteUser.messages.failed')}
        >
            {content}
        </StickyFooterModal>
    );
};

const useDeleteUserModal = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [entitiesAssignedToUser, setEntitiesAssignedToUser] = useState<DeleteUserMutation['result']>();

    const actions = useMemo(
        () => ({
            open: (deleteUserResult: DeleteUserMutation['result']) => {
                setEntitiesAssignedToUser(deleteUserResult);
                setIsModalOpen(true);
            },
            close: () => setIsModalOpen(false),
        }),
        [setIsModalOpen]
    );

    return {
        ...actions,
        render: (props: ModalProps & Pick<DeleteUserModalProps, 'displayCompany' | 'userId' | 'onRetry'>) => (
            <DeleteUserModal
                {...props}
                deleteUserResult={entitiesAssignedToUser}
                onClose={actions.close}
                open={isModalOpen}
            />
        ),
    };
};

export default useDeleteUserModal;
